import React, {ForwardedRef, useMemo, useState} from "react";
import {Box, FormControl, FormLabel, TextField, Typography} from "@mui/material";
import {TextFieldProps} from "@mui/material/TextField/TextField";

type TextInputProps = TextFieldProps & {
    hint?: string;
    errorMessage?: string;
    fullHeight?: boolean;
    readOnly?: boolean;
    optional?: boolean;
};

export const TextInput = React.forwardRef((props: TextInputProps, ref: ForwardedRef<HTMLInputElement>) => {

    const {
        label,
        id,
        focused,
        error,
        onChange,
        onFocus,
        onBlur,
        errorMessage,
        hint,
        fullHeight,
        readOnly,
        optional,
        InputProps,
        className,
        ...rest
    } = props;

    const [isFocused, setIsFocused] = useState(readOnly ? false : focused);

    const InternalInputProps = useMemo(
        () => ({
            sx: fullHeight ? {flex: 1, alignItems: "flex-start"} : undefined,
            readOnly: readOnly,
            ...InputProps,
        }),
        [fullHeight, readOnly, InputProps]
    );
    
    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const value = ev.target.value;
        onChange?.(ev);
    };

    return (
        <Box className={className} sx={fullHeight ? {flex: 1, display: "flex", flexDirection: "column"} : {}}>
            <FormControl error={error} fullWidth>
                {label && (
                    <FormLabel error={error} focused={isFocused} htmlFor={id} sx={{whiteSpace: "pre-wrap", mb: 0.5}} className={"input-label"}>
                        {label}
                    </FormLabel>
                )}
                <TextField
                    inputRef={ref}
                    className={"input-field"}
                    margin="none"
                    error={error}
                    required={false}
                    fullWidth
                    focused={focused}
                    onChange={handleChange}
                    onFocus={(ev) => {
                        setIsFocused(!readOnly);
                        onFocus?.(ev);
                    }}
                    onBlur={(ev) => {
                        setIsFocused(false);
                        onBlur?.(ev);
                    }}
                    id={id}
                    sx={fullHeight ? {flex: 1} : undefined}
                    InputProps={InternalInputProps}
                    inputProps={fullHeight ? {sx: {height: "100% !important"}} : undefined}
                    {...rest}
                />
                {props.errorMessage && (
                    <Typography variant={"body2"} className={"error-message"} component="div">
                        {errorMessage}
                    </Typography>
                )}
                {props.hint && (
                    <Typography variant={"body2"} className={"input-help"} component="div" sx={{marginTop: 0.5}}>
                        {hint}
                    </Typography>
                )}
            </FormControl>
        </Box>
    );
});

